import React, { useState, useCallback } from 'react';
import messages from '../messages';
import bankDetailsMsg from '../../../BankDetails/messages';
import { useIntl } from 'react-intl';
import { jsonApiAxios, useHandleError } from '../../../../services/api';
import useAuthenticate from '../../../../hooks/useAuthenticate';
import Loading from '../../../../components/Loading';
import Alert from '../../../../components/Alert';
import { useModal } from '../../../../components/Modals/ModalContext';
import CircleXIcon from '../../../../assets/icons/circle-x.svg';
import moment from 'moment';

interface PlanProps {
  subscription: {
    ends_at: Date | undefined;
    next_billing_date: string;
  };
}

const Plan = (props: PlanProps) => {
  const { subscription } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const { formatMessage } = useIntl();
  const [handleError] = useHandleError();
  const { token } = useAuthenticate();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [showModal] = useModal('default');

  const handleCancelAutoRenew = useCallback(() => {
    showModal({
      title: formatMessage(messages.cancelAutoRenewTitleModal),
      body: (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span>{formatMessage(messages.cancelAutoRenewSubTitleModal)}</span>
          </div>
          <div className="flex justify-around flex-col md:flex-row">
            <div style={{ display: 'flex' }}>
              <img src={CircleXIcon} />
              <span style={{ marginLeft: '2px' }}>
                {formatMessage(bankDetailsMsg.subscriptionCompenstationLabel)}
              </span>
            </div>
            <div style={{ display: 'flex' }}>
              <img src={CircleXIcon} />
              <span style={{ marginLeft: '2px' }}>
                {formatMessage(bankDetailsMsg.subscriptionCommissionLabel)}
              </span>
            </div>
            <div style={{ display: 'flex' }}>
              <img src={CircleXIcon} />
              <span style={{ marginLeft: '2px' }}>
                {formatMessage(messages.vipLoungeLabel)}
              </span>
            </div>
          </div>
        </div>
      ),
      onConfirm: cancelAutoRenew,
      onConfirmText: formatMessage(messages.autoRenewLabel),
      onCancelText: formatMessage(bankDetailsMsg.goBackLabel),
      className: 'md:max-w-680',
      confirmButtonClassName: 'modal-buttons-confirm',
      cancelButtonClassName: 'modal-buttons-cancel',
    });
    // eslint-disable-next-line
  }, []);

  const cancelAutoRenew = async () => {
    setIsLoading(true);
    try {
      const cancelled = await jsonApiAxios.post(
        '/subscriptionCancel',
        {},
        {
          schema: {},
          type: 'subscriptionCancel',
          headers,
        }
      );
      if (cancelled.status === 200) {
        setShowSuccessMsg(true);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '28px' }}>
      <span>{formatMessage(messages.planLabel)}</span>
      <div
        className="my-subscription-plan-container"
        style={{ flexDirection: subscription.ends_at ? 'column' : 'row' }}
      >
        <div className="my-subscription-plan-left">
          <span className="premium-header">
            {formatMessage(messages.premiumLabel)}
          </span>
          <span className="premium-subheader">
            {formatMessage(messages.priceLabel)}
          </span>
          <span className="premium-text">
            {formatMessage(messages.billedLabel)}
          </span>
          {!subscription.ends_at ? (
            <span className="premium-text">
              <b>{formatMessage(messages.nextPayment)}:</b>{' '}
              {moment(subscription.next_billing_date).format('DD/MM/YYYY')}
            </span>
          ) : (
            <span className="premium-text">
              <b>{formatMessage(messages.premiumDeadline)}:</b>{' '}
              {moment(subscription.ends_at).format('DD/MM/YYYY')}
            </span>
          )}
        </div>
        {!subscription.ends_at ? (
          <div className="my-subscription-plan-right">
            {isLoading ? (
              <div style={{ marginRight: '20px' }}>
                <Loading />
              </div>
            ) : (
              <button
                className="button-secondary-subscription"
                onClick={handleCancelAutoRenew}
              >
                {formatMessage(messages.autoRenewLabel)}
              </button>
            )}
            {showSuccessMsg ? (
              <Alert level="success" className="w-full my-20">
                {formatMessage(messages.autoRenewLabel)}
              </Alert>
            ) : null}
          </div>
        ) : (
          <div style={{ padding: '12px 8px' }}>
            <div className="auto-renewal-cancelled">
              <span className="text">
                {formatMessage(messages.cancelledAutoRenewalMsg)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Plan;
